import React, { useMemo } from 'react';
import {
  SandpackLayout,
  SandpackPreview as SandpackPreviewFrame,
  SandpackProvider,
} from '@codesandbox/sandpack-react';
import { nightOwl } from '@codesandbox/sandpack-themes';
import { filesToSandpack, resolveSandpackTemplate, resolveSandpackEntry, isSandpackDefaultApp, isSandpackRenderable } from '../../utils/previewHtml';

function SandpackFallback({ message = 'Preview unavailable — waiting for live server…' }) {
  return (
    <div className="flex h-full min-h-[320px] items-center justify-center rounded-lg border border-dashed border-zinc-700 bg-zinc-900/50 px-6 text-center text-sm text-zinc-400">
      {message}
    </div>
  );
}

export default function SandpackPreview({
  files,
  projectName = 'App',
  previewVersion = 0,
}) {
  const sandpackFiles = useMemo(() => filesToSandpack(files, projectName), [files, projectName]);
  const template = useMemo(() => resolveSandpackTemplate(files), [files]);
  const entry = useMemo(() => resolveSandpackEntry(files), [files]);
  const fileKeys = useMemo(
    () => Object.keys(sandpackFiles || {}).filter((k) => !k.startsWith('__')),
    [sandpackFiles],
  );

  if (!sandpackFiles || fileKeys.length < 2) {
    return <SandpackFallback message="Not enough project files to preview yet." />;
  }

  if (!isSandpackRenderable(files, projectName)) {
    return <SandpackFallback />;
  }

  const appCode = sandpackFiles['/src/App.tsx']?.code
    || sandpackFiles['/src/App.jsx']?.code
    || files?.['src/App.tsx']
    || files?.['src/App.jsx']
    || '';
  const hasRealApp = appCode && !isSandpackDefaultApp(appCode);
  const hasPageFiles = Object.keys(files || {}).some((p) => p.replace(/\\/g, '/').includes('/pages/'));
  if (!hasRealApp && !hasPageFiles) {
    return <SandpackFallback />;
  }

  const useTemplateOnly = fileKeys.length < 4;

  return (
    <SandpackProvider
      key={`sandpack-${previewVersion}-${fileKeys.join(',')}`}
      template={useTemplateOnly ? template : undefined}
      theme={nightOwl}
      files={sandpackFiles}
      customSetup={entry ? { entry } : undefined}
      options={{
        autorun: true,
        autoReload: true,
        recompileMode: 'immediate',
        recompileDelay: 300,
      }}
    >
      <SandpackLayout style={{ height: '100%', minHeight: 320, border: 'none', borderRadius: 8 }}>
        <SandpackPreviewFrame
          showOpenInCodeSandbox={false}
          showRefreshButton
          style={{ height: '100%' }}
        />
      </SandpackLayout>
    </SandpackProvider>
  );
}
